import { useState } from "react";
import { Minus, Plus, Lock, Calculator, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContact } from "@/components/ContactModal";

/**
 * Public pricing calculator. Pure UI — no backend calls, no Stripe, no
 * billing. Pulls live totals from the same plan price model used on the
 * /priser cards and the Staff page (+99 kr/mnd per ekstra ansatt eks. mva).
 *
 * Lives on the public landing page so prospects can quickly estimate
 * what Pro / Premium will cost them with N employees before contacting
 * us. Basic is intentionally not interactive — it's a 1-bruker plan and
 * the calculator nudges users toward Pro for multi-staff use cases.
 */

const PLANS = [
  { id: "basic",   label: "Basic",   price: 199, allowsExtra: false },
  { id: "pro",     label: "Pro",     price: 399, allowsExtra: true,  featured: true },
  { id: "premium", label: "Premium", price: 599, allowsExtra: true },
];
const EXTRA_PER_STAFF = 99;
const MAX_EXTRA = 10;

function formatKr(n) {
  // Norwegian thousands separator (space). "1 296" not "1,296".
  return n.toLocaleString("nb-NO");
}

export default function PriceCalculator() {
  const { open: openContact } = useContact();
  const [planId, setPlanId] = useState("pro");
  const [extra, setExtra] = useState(2);
  const plan = PLANS.find((p) => p.id === planId);
  const effectiveExtra = plan.allowsExtra ? extra : 0;
  const extraCost = effectiveExtra * EXTRA_PER_STAFF;
  const total = plan.price + extraCost;

  const dec = () => setExtra((v) => Math.max(0, v - 1));
  const inc = () => setExtra((v) => Math.min(MAX_EXTRA, v + 1));

  // Build the pre-filled message based on the current calculator state.
  // Mirrors the receipt block visually so the recipient sees exactly the
  // same numbers the prospect saw on the page.
  const buildPrefilledMessage = () => {
    const lines = [
      "Hei, jeg ønsker tilbud på ClientFlow.",
      "",
      `Valgt pakke: ${plan.label}`,
    ];
    if (plan.allowsExtra) {
      lines.push(`Ekstra ansatte: ${effectiveExtra}`);
      lines.push(`Grunnpris: ${formatKr(plan.price)} kr/mnd`);
      if (effectiveExtra > 0) {
        lines.push(
          `Ekstra ansatte: ${effectiveExtra} × 99 kr = ${formatKr(extraCost)} kr/mnd`
        );
      }
    } else {
      lines.push("Ekstra ansatte: ikke aktuelt (Basic er for 1 bruker/leder)");
      lines.push(`Grunnpris: ${formatKr(plan.price)} kr/mnd`);
    }
    lines.push(`Estimert månedspris: ${formatKr(total)} kr eks. mva`);
    lines.push("");
    lines.push("Estimat. Endelig pris bekreftes før betaling.");
    lines.push("Kilde: Priskalkulator");
    return lines.join("\n");
  };

  const handleRequestQuote = () => {
    openContact({
      source: "pricing_calculator",
      defaultInterest: plan.id,
      defaultMessage: buildPrefilledMessage(),
    });
  };

  return (
    <div
      className="mt-16 max-w-3xl mx-auto rounded-3xl border border-border bg-card shadow-sm overflow-hidden"
      data-testid="price-calculator"
    >
      <div className="p-6 sm:p-8 lg:p-10">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Calculator className="w-5 h-5" strokeWidth={1.75} />
          </span>
          <div>
            <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">
              Beregn din månedspris
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Velg pakke og antall ekstra ansatte for å se hva du betaler.
            </p>
          </div>
        </div>

        {/* Plan picker — pill buttons. Stacks 2/1 on mobile, side-by-side
            on ≥sm so the 3 plans share equal width. */}
        <div
          className="mt-7 grid grid-cols-3 gap-2 sm:gap-3 p-1.5 rounded-2xl bg-accent/50 border border-border/60"
          role="radiogroup"
          aria-label="Velg pakke"
        >
          {PLANS.map((p) => {
            const active = planId === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setPlanId(p.id)}
                className={`relative px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active
                    ? "bg-card text-foreground shadow-sm ring-1 ring-border"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`calc-plan-${p.id}`}
              >
                <span className="block font-heading">{p.label}</span>
                <span className={`block text-[11px] tabular-nums mt-0.5 ${active ? "text-muted-foreground" : "text-muted-foreground/70"}`}>
                  {p.price} kr/mnd
                </span>
              </button>
            );
          })}
        </div>

        {/* Extra-staff control. Locked + nudge on Basic; otherwise a
            stepper with min/max guards. */}
        <div className="mt-7">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-foreground">
                Ekstra ansatte
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {plan.allowsExtra
                  ? "99 kr/mnd eks. mva per ekstra ansatt."
                  : "Basic er for én bruker / leder."}
              </p>
            </div>

            {plan.allowsExtra ? (
              <div
                className="flex items-center gap-1 rounded-xl border border-border bg-background p-1"
                data-testid="calc-extra-stepper"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={dec}
                  disabled={effectiveExtra <= 0}
                  className="h-9 w-9 rounded-lg"
                  aria-label="Færre ansatte"
                  data-testid="calc-extra-dec"
                >
                  <Minus className="w-4 h-4" strokeWidth={1.75} />
                </Button>
                <span
                  className="min-w-[2.5rem] text-center font-heading text-lg tabular-nums"
                  data-testid="calc-extra-count"
                >
                  {effectiveExtra}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={inc}
                  disabled={effectiveExtra >= MAX_EXTRA}
                  className="h-9 w-9 rounded-lg"
                  aria-label="Flere ansatte"
                  data-testid="calc-extra-inc"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.75} />
                </Button>
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-medium" data-testid="calc-extra-locked">
                <Lock className="w-3.5 h-3.5" strokeWidth={1.75} /> Ikke tilgjengelig
              </span>
            )}
          </div>

          {!plan.allowsExtra && (
            <div
              className="mt-3 rounded-xl border border-border bg-accent/30 px-4 py-3 text-sm"
              data-testid="calc-basic-note"
            >
              <p className="text-foreground">
                Basic inkluderer <span className="font-semibold">1 bruker / leder</span>.
              </p>
              <button
                type="button"
                onClick={() => setPlanId("pro")}
                className="text-primary font-medium hover:underline text-sm mt-1"
                data-testid="calc-basic-upgrade"
              >
                Oppgrader til Pro for flere ansatte →
              </button>
            </div>
          )}
        </div>

        {/* Receipt block — calm summary, tabular-nums for clean alignment. */}
        <div className="mt-7 rounded-2xl border border-border bg-background p-5 sm:p-6">
          <dl className="space-y-2.5 text-sm tabular-nums">
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted-foreground">
                <span className="font-medium text-foreground">{plan.label}</span> grunnpris
              </dt>
              <dd className="font-medium" data-testid="calc-base-price">{formatKr(plan.price)} kr</dd>
            </div>
            <div className={`flex items-baseline justify-between gap-4 transition-opacity ${effectiveExtra > 0 ? "opacity-100" : "opacity-60"}`}>
              <dt className="text-muted-foreground">
                {effectiveExtra > 0
                  ? <>Ekstra ansatte <span className="text-foreground/70">({effectiveExtra} × 99 kr)</span></>
                  : "Ekstra ansatte"}
              </dt>
              <dd className="font-medium" data-testid="calc-extra-cost">+ {formatKr(extraCost)} kr</dd>
            </div>
          </dl>

          <div className="mt-4 pt-4 border-t border-border flex items-baseline justify-between gap-4">
            <span className="font-heading text-base font-semibold">Total</span>
            <div className="text-right">
              <div className="font-heading text-3xl font-bold tabular-nums" data-testid="calc-total">
                {formatKr(total)} <span className="text-base font-medium text-muted-foreground">kr/mnd</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">eks. mva</div>
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleRequestQuote}
          className="mt-5 w-full rounded-xl border-border bg-background hover:bg-accent/50"
          data-testid="calc-request-quote"
        >
          <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Send meg tilbud på e-post
        </Button>

        <p className="mt-3 text-xs text-muted-foreground text-center" data-testid="calc-disclaimer">
          Estimat. Endelig pris bekreftes før betaling.
        </p>
      </div>
    </div>
  );
}
